import { useState, useEffect } from "react";
import { Typography as T } from "@mui/material";
import { IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import Button from "../../../components/Button";
import { toast } from "react-toastify";
import { colors } from "../../../styles";
import useGetOnboarding from "../../../hooks/queries/admin/useGetOnboarding";
import { StepComponentProps } from "./index";
import api from "../../../api/api";
import upload from "../../../assets/icons/greenIcons/upload.svg";
import closeIcon from "../../../assets/icons/close-icon.svg";
import { ReactComponent as FileExchangeImg } from "../../../assets/images/fileExchange.svg";
import { ReactComponent as ColumnsTableImg } from "../../../assets/images/uploadDirectoryTable.svg";
import { QUERY_ONBOARDING_KEY } from "../../../api/queryKeys";

const Container = styled("div")(`
  height: 100vh;
  width: 100%;
`);

const InnerContainer = styled("div")(`
  width: 60%;
  margin: auto;
`);

const FormatDetails = styled("p")(`
  margin: 6px auto;
  width: fit-content;
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  color: #475467;
  cursor: pointer;
`);

const DropBox = styled("div")(`
  height: 160px;
  border: 1px dashed ${colors.gray};
  border-radius: 8px;
  background: ${colors.white};
  margin: 8px auto;
`);

const PageTitle = styled("h2")(`
  font-family: Inter;
  font-weight: 600;
  font-size: 20px;
  text-align: center;
  margin: 70px auto 0px;
`);

const PageSubTitle = styled("p")(`
  font-family: Inter;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #475467;
  width: 60%;
  margin: 10px auto;
`);

const UploadHeader = styled("h2")(`
  font-family: Inter;
  font-style: normal;
  font-weight: 500;
  display: flex;
  justify-content: center;
  font-size: 14px;
  line-height: 17px;
  color: ${colors.black};
  margin: 0;
`);

const UploadImg = styled("img")(`
  width: 50px;
  display: flex;
  margin: 30px auto 10px;
`);

const FileInfoRow = styled("div")(`
  display: flex;
  width: 100%;
  margin: 10px 0 0;
  justifyContent: space-between;
  alignItems: center;
  fontFamily: Inter;
  fontSize: 14px;
  color: #758790;
`);

const FileInfo = styled("div")(`
  display: flex;
  flexDirection: row;
  justifyContent: space-between;
  width: 90px;
  alignItems: center;
`);

const ImgContainer = styled("div")(`
  width: 100%;
  margin-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
`);

const Payroll = ({ nextStep }: StepComponentProps) => {
  const [payrollStep, setPayrollStep] = useState(0);
  const [emphasis, setEmphasis] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<any>(null);

  // Queries
  const { data } = useGetOnboarding();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const allowDrop = (event: any) => {
    setEmphasis(!emphasis);
    event.preventDefault();
  };

  const drop = (event: any) => {
    event.preventDefault();
    const fileData = event.dataTransfer.files[0];
    setSelectedFile(fileData);
    setEmphasis(false);
  };

  const handleContinue = async () => {
    // they have a file already uploaded
    if (!selectedFile && data?.employer?.employee_list_file) {
      nextStep();
      return;
    }
    // they need to provide a file
    if (!selectedFile && !data?.employer?.employee_list_file) {
      toast.error("Please select a file to upload.");
      return;
    }
    // upload new file
    const form_data = new FormData();
    form_data.append("employee_list_file", selectedFile, selectedFile.name);
    try {
      await api.put(QUERY_ONBOARDING_KEY, form_data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      nextStep();
    } catch (error) {
      toast.error("Error uploading file. Please try again.");
    }
  };

  if (data) {
    return (
      <Container>
        {payrollStep === 0 ? (
          <>
            <PageTitle>
              For {data.employer?.payroll_system} customers, we support a manual
              file exchange process.
            </PageTitle>
            <PageSubTitle>
              We are working to support an automated payroll integration.
            </PageSubTitle>
            <InnerContainer>
              <div>
                <ImgContainer>
                  <FileExchangeImg />
                </ImgContainer>
                <Button
                  children={"Continue"}
                  onClick={() => setPayrollStep(payrollStep + 1)}
                />
              </div>
            </InnerContainer>
          </>
        ) : (
          <>
            <PageTitle>Upload your Company Directory</PageTitle>
            <PageSubTitle>
              Please upload a spreadsheet of current employees. See example
              below for required and optional fields.
            </PageSubTitle>
            <ImgContainer style={{ marginBottom: 30 }}>
              <ColumnsTableImg />
            </ImgContainer>
            <InnerContainer>
              <DropBox onDrop={drop} onDragOver={allowDrop}>
                <UploadImg src={upload} alt="upload" />
                <UploadHeader>
                  <label
                    style={{ cursor: "pointer" }}
                    htmlFor="upload-file-button"
                  >
                    Click to upload
                  </label>
                  <input
                    type="file"
                    id="upload-file-button"
                    accept=".csv, .xlsx"
                    hidden
                    onChange={(e: any) => setSelectedFile(e.target.files[0])}
                  />
                  &nbsp;{" "}
                  <p style={{ margin: "0", fontWeight: 400 }}>
                    or drag and drop
                  </p>
                </UploadHeader>
                <FormatDetails>
                  Accepted file types include .csv and .xlsx
                </FormatDetails>
                {selectedFile && (
                  <FileInfoRow>
                    <p>{selectedFile.name}</p>
                    <FileInfo>
                      <IconButton
                        onClick={() => {
                          setSelectedFile(null);
                        }}
                      >
                        <img
                          style={{ marginTop: 4, width: 10 }}
                          src={closeIcon}
                          alt="close icon"
                        />
                      </IconButton>
                    </FileInfo>
                  </FileInfoRow>
                )}
              </DropBox>
              {data.employer?.employee_list_file && (
                <T variant="mdReg" color={"#758790"}>
                  You've already uploaded a file. You can upload a different one
                  if you need.
                </T>
              )}
              <Button
                disabled={!selectedFile && !data.employer?.employee_list_file}
                onClick={handleContinue}
              >
                Continue
              </Button>
              {!data.employer?.employee_list_file && (
                <p style={{ fontSize: 12, textAlign: "center", marginTop: 30 }}>
                  Don't have this right now?{" "}
                  <span
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                    onClick={nextStep}
                  >
                    Skip and do it later.
                  </span>
                </p>
              )}
            </InnerContainer>
          </>
        )}
      </Container>
    );
  }
  return null;
};

export default Payroll;
